import React, { useEffect, useRef, useState } from "react";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import MetaTags from "react-meta-tags";
import Hero from "./components/Hero";
import SelfOrder from "./components/SelfOrder";
import RegisterCTA from "../../components/buttons/RegisterCTA";
import SalesCTA from "../../components/buttons/SalesCTA";

// Component to trigger smooth slide-in animation on scroll
function AnimatedHighlightRow({ hl, idx }) {
  const rowRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => {
      if (rowRef.current) observer.unobserve(rowRef.current);
    };
  }, []);

  const textAnimationClass = isVisible
    ? hl.reverse
      ? "slide-right-active"
      : "slide-left-active"
    : "";

  const imageAnimationClass = isVisible
    ? hl.reverse
      ? "slide-left-active"
      : "slide-right-active"
    : "";

  return (
    <div ref={rowRef} style={{ overflow: "hidden", padding: "10px 0" }}>
      <Grid
        container
        spacing={6}
        alignItems="center"
        justify="space-between"
        direction={hl.reverse ? "row-reverse" : "row"}
      >
        {/* Text Col */}
        <Grid
          item
          xs={12}
          md={6}
          className={textAnimationClass}
          style={{ opacity: isVisible ? 1 : 0 }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#DE148C",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            {hl.kicker}
          </div>

          <Typography
            variant="h3"
            style={{
              fontWeight: 800,
              color: "#0f172a",
              fontSize: "clamp(1.6rem, 2.5vw, 2.1rem)",
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
              marginBottom: "16px",
            }}
          >
            {hl.title}
          </Typography>

          <Typography
            variant="body1"
            style={{
              color: "#64748b",
              fontSize: "15px",
              lineHeight: 1.7,
              marginBottom: "20px",
            }}
          >
            {hl.desc}
          </Typography>

          {/* Bullets */}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {hl.bullets.map((bullet, bIdx) => (
              <div
                key={bIdx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(34, 197, 94, 0.12)",
                    color: "#16a34a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  ✓
                </span>
                <span
                  style={{
                    color: "#334155",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {bullet}
                </span>
              </div>
            ))}
          </div>
        </Grid>

        {/* Mockup Image Col */}
        <Grid
          item
          xs={12}
          md={5}
          className={imageAnimationClass}
          style={{
            textAlign: "center",
            opacity: isVisible ? 1 : 0,
          }}
        >
          <img
            src={hl.image}
            alt={hl.title}
            style={{
              maxHeight: "430px",
              maxWidth: "100%",
              height: "auto",
              borderRadius: "12px",
              display: "inline-block",
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default function Index() {
  const partners = [
    { name: "Bakso Jongker", src: "./asset/images/partners/Bakso_Jongker.png" },
    { name: "BMC 1928", src: "./asset/images/partners/BMC_1928.png" },
    { name: "Braga Sky 1957", src: "./asset/images/partners/Braga_Sky_1957.png" },
    { name: "Fullmoon Coffee", src: "./asset/images/partners/Fullmoon_Coffee.png" },
    { name: "Giat Mart", src: "./asset/images/partners/Giat_Mart.png" },
    { name: "Hog Chi Minh", src: "./asset/images/partners/Hog_Chi_Minh.png" },
    { name: "Riau 20", src: "./asset/images/partners/Riau_20.png" },
    { name: "Selera 1686", src: "./asset/images/partners/Selera_1686.png" },
    { name: "Tumbler Coffee", src: "./asset/images/partners/Tumbler_Coffee.png" },
    { name: "Tumbler Coffee & Gelato", src: "./asset/images/partners/Tumbler_Coffee_Gelato.png" },
    { name: "Umbira", src: "./asset/images/partners/Umbira.png" },
    { name: "WellGrow Coffee", src: "./asset/images/partners/WellGrow_Coffee.png" },
    { name: "Amara Bistros", src: "./asset/images/partners/Amara_Bistros.png" },
    { name: "NOB Coffees", src: "./asset/images/partners/NOB_Coffees.png" },
    { name: "Orte Cafe", src: "./asset/images/partners/Orte_Cafe.png" },
  ];

  // Bagi array partner menjadi 2 kelompok
  const halfLength = Math.ceil(partners.length / 2);
  const partnersRow1 = partners.slice(0, halfLength);
  const partnersRow2 = partners.slice(halfLength);

  // Duplikasi 4x agar panjang elemen melebihi lebar layar monitor besar (mencegah jeda kosong)
  const marqueeRow1 = [
    ...partnersRow1,
    ...partnersRow1,
    ...partnersRow1,
    ...partnersRow1,
  ];
  const marqueeRow2 = [
    ...partnersRow2,
    ...partnersRow2,
    ...partnersRow2,
    ...partnersRow2,
  ];

  // Component Reusable untuk Card Partner
  const PartnerCard = ({ partner }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        padding: "12px",
        transition: "transform 0.2s ease, opacity 0.2s ease",
        cursor: "pointer",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.opacity = "0.8";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.opacity = "1";
      }}
    >
      <img
        src={partner.src}
        alt={partner.name}
        style={{
          maxHeight: "100px",
          maxWidth: "220px",
          objectFit: "contain",
          display: "block",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      />
    </div>
  );

  const features = [
    {
      title: "Multi Platform & Cloud",
      desc: "Aplikasi POS kasir tersedia di Android, iOS, dan Backoffice Web yang dapat diakses kapan saja dari mana saja.",
      icon: "./asset/images/icons/platform.webp",
    },
    {
      title: "Menu Digital Real-time",
      desc: "Perbarui daftar hidangan, foto, harga, dan varian menu secara instan tanpa perlu mencetak ulang buku fisik.",
      icon: "./asset/images/icons/digital-menu.webp",
    },
    {
      title: "Pemesanan Mandiri (Self-Order)",
      desc: "Pelanggan memesan dan membayar langsung dari meja makan mereka untuk mengurangi antrian kasir di jam sibuk.",
      icon: "./asset/images/icons/mobile-ordering.webp",
    },
    {
      title: "Pembayaran Digital Terintegrasi",
      desc: "Mendukung QRIS, e-Wallet, kartu debit/kredit, dan tunai dalam rekonsiliasi satu pintu yang akurat.",
      icon: "./asset/images/icons/digital-payment.webp",
    },
    {
      title: "Laporan Penjualan & Analitik",
      desc: "Pantau omset harian, tren menu terlaris, jam ramai transaksi, dan margin laba secara visual.",
      icon: "./asset/images/icons/report-analysis.webp",
    },
    {
      title: "Service Calling Staf",
      desc: "Pelanggan dapat memanggil pelayan atau meminta bill dari meja untuk meningkatkan kecepatan pelayanan.",
      icon: "./asset/images/icons/service-calling.webp",
    },
    {
      title: "Split Bill Otomatis",
      desc: "Memudahkan rombongan pelanggan membagi tagihan per orang secara mandiri dan langsung bayar per item.",
      icon: "./asset/images/icons/split-bills.webp",
    },
    {
      title: "Kartu Member & Loyalty",
      desc: "Kumpulkan poin transaksi, reward otomatis, dan kirim kupon promo untuk menjaga retensi pelanggan.",
      icon: "./asset/images/icons/member-card.webp",
    },
    {
      title: "Notifikasi Status Pesanan",
      desc: "Pelanggan mendapat notifikasi instan saat hidangan selesai dimasak dan siap diantar atau diambil.",
      icon: "./asset/images/icons/notification.webp",
    },
  ];

  const highlights = [
    {
      kicker: "QR CODE SCANNING",
      title: "Pesan Instan dari Meja Tanpa Repot",
      desc: "Cukup dengan mengarahkan kamera smartphone ke kode QR di meja, pelanggan langsung melihat daftar menu restoran dan dapat memesan hidangan dalam hitungan detik.",
      bullets: [],
      image: "./asset/images/features/fitur-scan.webp",
      reverse: false,
    },
    {
      kicker: "DIGITAL MENU",
      title: "Katalog Menu yang Selalu Terbarukan",
      desc: "Buku menu cetak mudah rusak dan memerlukan biaya setiap ada perubahan. Dengan UR Digital Menu, seluruh update harga dan stok langsung tampil secara real-time.",
      bullets: [],
      image: "./asset/images/features/digital-menu.webp",
      reverse: true,
    },
    {
      kicker: "SMART WAITER",
      title: "Rekomendasi Menu & Upselling Otomatis",
      desc: "Tingkatkan rata-rata nilai transaksi dengan fitur rekomendasi otomatis Smart Waiter yang menawarkan pairing menu, minuman pendamping, atau hidangan best-seller.",
      bullets: [],
      image: "./asset/images/features/smart-waiter.webp",
      reverse: false,
    },
    {
      kicker: "INTEGRATED PAYMENT",
      title: "Pembayaran Digital Cepat & Aman",
      desc: "UR telah terintegrasi dengan beragam payment gateway dan e-Wallet ternama. Pelanggan dapat membayar langsung dari smartphone dengan aman.",
      bullets: [],
      image: "./asset/images/features/digital-payment.webp",
      reverse: true,
    },
    {
      kicker: "TRANSACTION AUDIT",
      title: "Detail Riwayat Transaksi Lengkap",
      desc: "Transparansi penuh untuk pelanggan dan pemilik resto. Setiap transaksi tercatat secara detail dan terarsip rapi untuk keperluan rekonsiliasi kasir.",
      bullets: [],
      image: "./asset/images/features/order-history.webp",
      reverse: false,
    },
    {
      kicker: "MEMBERSHIP",
      title: "Program Loyalitas & Retensi Pelanggan",
      desc: "Bangun hubungan jangka panjang dengan pengunjung restoran Anda melalui sistem keanggotaan digital, poin reward, dan promo berkala.",
      bullets: [],
      image: "./asset/images/features/loyalty-rewards.webp",
      reverse: true,
    },
  ];

  return (
    <div style={{ maxWidth: "100%", overflowX: "hidden", backgroundColor: "#ffffff" }}>
      <MetaTags>
        <title>UR : Aplikasi POS &amp; Self Order untuk Restoran Modern</title>
        <meta
          name="title"
          content="UR : Aplikasi POS & Self Order untuk Restoran Modern"
        />
        <meta
          name="description"
          content="UR memberikan solusi modern untuk sistem pemesanan self order dan memudahkan restoran mengolah transaksi kasir secara real-time."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ur-hub.com/" />
        <meta
          property="og:title"
          content="UR : Aplikasi POS & Self Order untuk Restoran Modern"
        />
        <meta
          property="og:image"
          content="https://res.cloudinary.com/urhub/image/upload/v1791947764/logo-white-only.webp"
        />
      </MetaTags>

      {/* Hero Section */}
      <Hero />

      {/* Partner Logo Animated Showcase (2 Rows) */}
      <section
        style={{
          padding: "64px 0",
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Left & Right subtle edge fade overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "120px",
            height: "100%",
            background: "linear-gradient(to right, #f8fafc, transparent)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "120px",
            height: "100%",
            background: "linear-gradient(to left, #f8fafc, transparent)",
            zIndex: 10,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" style={{ marginBottom: "32px" }}>
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                color: "#64748b",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                margin: 0,
              }}
            >
              Dipercaya oleh ratusan brand kuliner terkemuka di Indonesia
            </p>
          </div>
        </Container>

        {/* Marquee Container */}
        <div style={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: "20px" }}>
          
          {/* Row 1: Bergerak Kiri ke Kanan (LTR) */}
          <div
            className="animate-marquee-ltr"
            style={{
              display: "flex",
              width: "max-content",
              gap: "24px",
            }}
          >
            {marqueeRow1.map((partner, index) => (
              <PartnerCard key={`row1-${index}`} partner={partner} />
            ))}
          </div>

          {/* Row 2: Bergerak Kanan ke Kiri (RTL) */}
          <div
            className="animate-marquee-rtl"
            style={{
              display: "flex",
              width: "max-content",
              gap: "24px",
            }}
          >
            {marqueeRow2.map((partner, index) => (
              <PartnerCard key={`row2-${index}`} partner={partner} />
            ))}
          </div>

        </div>
      </section>

      {/* Self Order Showcase */}
      <SelfOrder />

      {/* Features Grid Section */}
      <section
        style={{
          padding: "80px 0",
          backgroundColor: "#ffffff",
        }}
      >
        <Container maxWidth="lg">
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div
              style={{
                color: "#DE148C",
                fontSize: "13px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "8px",
              }}
            >
              Fitur Lengkap
            </div>

            <Typography
              variant="h2"
              style={{
                fontWeight: 800,
                color: "#0f172a",
                fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)",
                letterSpacing: "-0.02em",
                marginBottom: "12px",
              }}
            >
              Satu Ekosistem untuk Semua Kebutuhan Restoran
            </Typography>

            <p
              style={{
                color: "#64748b",
                fontSize: "15px",
                maxWidth: "600px",
                margin: "0 auto",
                lineHeight: 1.6,
              }}
            >
              Dari pemesanan meja, kasir, dapur, hingga laporan keuangan, semua terhubung otomatis dan dapat dipantau dari satu dashboard.
            </p>
          </div>

          <Grid container spacing={3}>
            {features.map((feat, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "14px",
                    padding: "24px",
                    height: "100%",
                    border: "1px solid #e2e8f0",
                    transition: "all 0.2s ease",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#cbd5e1";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.04)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "10px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <img
                      src={feat.icon}
                      alt={feat.title}
                      style={{
                        width: "28px",
                        height: "28px",
                        objectFit: "contain",
                      }}
                    />
                  </div>

                  <Typography
                    variant="h5"
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      fontSize: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    {feat.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    style={{
                      color: "#64748b",
                      fontSize: "14px",
                      lineHeight: 1.6,
                    }}
                  >
                    {feat.desc}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Feature Deep-Dives with Smooth Directional Slide-In on Scroll */}
      <section
        style={{
          padding: "80px 0",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Container maxWidth="lg">
          <div style={{ display: "flex", flexDirection: "column", gap: "72px" }}>
            {highlights.map((hl, idx) => (
              <AnimatedHighlightRow key={idx} hl={hl} idx={idx} />
            ))}
          </div>
        </Container>
      </section>

      {/* Bottom Conversion Banner */}
      <section
        style={{
          padding: "72px 0",
          backgroundColor: "#0f172a",
          color: "#ffffff",
        }}
      >
        <Container maxWidth="md" style={{ textAlign: "center" }}>
          <Typography
            variant="h2"
            style={{
              fontWeight: 800,
              fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
              marginBottom: "14px",
            }}
          >
            Siap Mengoptimalkan Operasional Restoran Anda?
          </Typography>

          <p
            style={{
              color: "#94a3b8",
              fontSize: "15px",
              lineHeight: 1.6,
              maxWidth: "540px",
              margin: "0 auto 32px auto",
            }}
          >
            Mulai uji coba gratis 14 hari tanpa biaya awal. Tim kami siap membantu persiapan menu dan konfigurasi meja.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <RegisterCTA
              padding="12px 28px"
              fontSize={15}
              text="Daftar Gratis Sekarang"
            />
            <SalesCTA
              padding="12px 24px"
              fontSize={15}
              text="Hubungi Sales"
            />
          </div>
        </Container>
      </section>
    </div>
  );
}